import { useEffect, useState } from "react";
import { useAuth } from "../../../context/contextAuth";
import { useDesign } from "../../../context/contextDesign";
import { useNetwork } from "../../../context/Dashboard/contextNetwork";
import { useChat } from "../../../context/contextChat";
import { useThemeMode } from "../../../context/contextTheme";
import CustomModal from "../../../components/modal";
import hexToRGBA from "../../../utils/hexToRGBA";
import { downloadNetworkCSV } from "../download";

const NetworkTable = () => {
  const { bHeightVal, zoom } = useDesign();
  const { theme } = useThemeMode();
  const { user } = useAuth();
  const { networkResults, currentIndex, setCurrentIndex } = useNetwork();
  const { setPrompt, setShowChat } = useChat();

  const [vulTable, setVulTable] = useState([]);
  const [modalContent, setModalContent] = useState("");
  const [showModal, setShowModal] = useState(false);

  // --- Helper: Severity Badge Styling ---
  const getSeverityStyle = (severity) => {
    const s = severity?.toLowerCase();
    if (s?.includes("high") || s?.includes("critical"))
      return { bg: hexToRGBA("#dc3545", 0.1), color: "#dc3545" };
    if (s?.includes("medium"))
      return { bg: hexToRGBA("#ffc107", 0.1), color: "#856404" };
    if (s?.includes("low"))
      return { bg: hexToRGBA("#198754", 0.1), color: "#198754" };
    return { bg: hexToRGBA(theme.textMuted, 0.1), color: theme.textMuted };
  };

  useEffect(() => {
    if (Array.isArray(networkResults) && networkResults.length > 0 && currentIndex !== null) {
      const scan = networkResults[currentIndex];
      setVulTable(scan?.vuln_data || []);
    }
  }, [currentIndex, networkResults]);

  const handleAIAssistance = (content) => {
    if (!content) return;
    const formattedPrompt = `Provide an extended technical breakdown suitable for a ${user?.designation}. \nExplain the network vulnerability: "${content}"`.trim();
    setPrompt(formattedPrompt);
    setShowChat(true);
    setShowModal(false);
  };

  return (
    <div className="d-flex flex-column h-100 overflow-hidden" style={{ zoom }}>
      {(!networkResults || networkResults.length === 0) ? (
        <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center opacity-50">
          <i className="bi bi-shield-slash display-1 mb-3"></i>
          <h4 className="fw-bold">Network Scan not yet started</h4>
        </div>
      ) : (
        <div className="d-flex flex-column h-100">

          {/* NATIVE SCAN SELECTOR */}
          <div
            className="px-4 py-3 flex-shrink-0 border-bottom"
            style={{ backgroundColor: theme.background }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <span className="small fw-bold text-muted text-uppercase">History:</span>
                <div className="d-flex bg-light p-1 rounded-3 overflow-auto no-scrollbar">
                  {networkResults.map((scan, index) => (
                    <button
                      key={scan.scan_uuid}
                      className="border-0 px-4 py-1 rounded-2 fw-bold transition-all"
                      style={{
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap",
                        backgroundColor: currentIndex === index ? "#344266" : "transparent",
                        color: currentIndex === index ? "white" : theme.textMuted,
                        boxShadow: currentIndex === index ? "0 2px 6px rgba(0,0,0,0.15)" : "none",
                      }}
                      onClick={() => setCurrentIndex(index)}
                    >
                      Scan {index + 1}
                    </button>
                  ))}
                </div>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2 fw-bold"
                  onClick={() => downloadNetworkCSV(networkResults, currentIndex)}
                >
                  <i className="bi bi-download"></i> Download CSV
                </button>
                {/* <div className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2">
                  Total Vulnerabilities: {networkResults[currentIndex].length}
                </div> */}
              </div>
            </div>
          </div>

          {/* NETWORK VULNERABILITY DATA GRID */}
          <div className="flex-grow-1 px-4 py-3 overflow-hidden">
            <div
              className="card border-0 rounded-4 shadow-sm h-100 overflow-hidden"
              style={{ backgroundColor: theme.background }}
            >
              <div className="table-responsive h-100 custom-scrollbar">
                <table className="table align-middle mb-0" style={{ color: theme.textColor }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#f8f9fa" }}>
                    <tr className="small text-muted text-uppercase fw-bolder" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                      <th className="ps-4 border-0 py-3">Vulnerability Title</th>
                      <th className="text-center border-0 py-3">Severity</th>
                      <th className="text-center border-0 py-3">Type/Platform</th>
                      {/* <th className="text-center border-0 py-3">Port</th> */}
                      <th className="text-center border-0 py-3">Impact</th>
                      <th className="pe-4 border-0 py-3 text-end">AI Analysis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vulTable.map((vuln, index) => {
                      const severity = getSeverityStyle(vuln.baseSeverity);
                      return (
                        <tr
                          key={index}
                          className="native-row-hover"
                          style={{ borderBottom: `1px solid ${hexToRGBA(theme.textColor, 0.05)}` }}>
                          <td className="ps-4 py-3" style={{ maxWidth: "300px" }}>
                            <div
                              className="fw-bold text-wrap"
                              style={{ fontSize: "0.85rem" }}>
                              {vuln.title}</div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "0.7rem" }}>
                              Codes: {vuln.codes || 'N/A'}
                            </div>
                          </td>
                          <td className="text-center">
                            <span className="badge rounded-pill px-3 py-1" style={{ backgroundColor: severity.bg, color: severity.color, fontSize: '0.65rem' }}>
                              {vuln.baseSeverity}
                            </span>
                          </td>
                          <td className="text-center small">
                            <div className="fw-medium">{vuln.type}</div>
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>{vuln.platform}</div>
                          </td>
                          {/* <td className="text-center small fw-bold text-secondary">
                            {vuln.port || '--'}
                          </td> */}
                          <td className="text-center">
                            <div className="small opacity-75">Exploit: {vuln.exploitability}</div>
                            <div className="small fw-bold">Impact: {vuln.impact}</div>
                          </td>
                          <td className="pe-4 py-3 text-end">
                            <button
                              className="btn btn-sm btn-light border shadow-sm rounded-pill px-3"
                              style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                              onClick={() => {
                                setModalContent(vuln.title);
                                setShowModal(true);
                              }}
                            >
                              <i className="bi bi-stars text-primary me-1"></i> Analyze
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL */}
      <CustomModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Network Security Analysis"
        footer={
          <div className="d-flex gap-2">
            <button className="btn btn-light fw-bold" onClick={() => setShowModal(false)}>Dismiss</button>
            <button className="btn btn-primary px-4 fw-bold" onClick={() => handleAIAssistance(modalContent)}>
              <i className="bi bi-stars me-2"></i>Generate Mitigation Plan
            </button>
          </div>
        }
      >
        <div className="p-2" style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: "1.6", color: "#444" }}>
          <h6 className="fw-bold mb-3 text-light">Vulnerability:</h6>
          <div className="bg-light p-3 rounded-3 border">{modalContent}</div>
          <p className="mt-3 small text-light">
            Clicking "Generate Mitigation Plan" will send this vulnerability data to the AI engine for a customized remediation strategy based on the role as {user?.designation}.
          </p>
        </div>
      </CustomModal>

      <style>{`
        .native-row-hover:hover {
          background-color: ${hexToRGBA("#344266", 0.02)};
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { 
          background: rgba(52, 66, 102, 0.1); 
          border-radius: 10px; 
        }
      `}</style>
    </div>
  );
};

export default NetworkTable;